import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../context/AuthContext";
import useApiStable from "../../utils/useApiStable";
import {sortTournamentsForOverview} from "./tournamentViewUtils";

const normalizeArray = (data) => Array.isArray(data) ? data : [];

export const useTournamentPageState = () => {
    const api = useApiStable();
    const {user} = useAuth();
    const navigate = useNavigate();

    const [tournaments, setTournaments] = useState([]);
    const [selectedTournamentId, setSelectedTournamentId] = useState(null);
    const [tournamentPlayers, setTournamentPlayers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [matches, setMatches] = useState([]);
    const [schedulerPair, setSchedulerPair] = useState(null);
    const [selectedFilterPlayerId, setSelectedFilterPlayerId] = useState("");
    const [editingMatch, setEditingMatch] = useState(null);
    const [isTournamentSelectionExpanded, setIsTournamentSelectionExpanded] = useState(true);
    const [knockoutPhaseExists, setKnockoutPhaseExists] = useState(false);
    const [knockoutBracketPlanned, setKnockoutBracketPlanned] = useState(false);
    const [myOverview, setMyOverview] = useState(null);
    const [myOverviewLoading, setMyOverviewLoading] = useState(false);
    const [myOverviewError, setMyOverviewError] = useState(null);
    const [registrationLoading, setRegistrationLoading] = useState(false);
    const [activeTournamentView, setActiveTournamentView] = useState("group");
    const [deepLinkMatchId, setDeepLinkMatchId] = useState(() => {
        const param = new URLSearchParams(window.location.search).get("matchId");
        const parsed = Number(param);
        return param && Number.isFinite(parsed) ? parsed : null;
    });
    const [deepLinkGroupId] = useState(() => {
        const param = new URLSearchParams(window.location.search).get("groupId");
        const parsed = Number(param);
        return param && Number.isFinite(parsed) ? parsed : null;
    });
    const latestTournamentSelectionRef = useRef(null);
    const filterAutoSetRef = useRef(false);

    const selectedTournament = useMemo(
        () => tournaments.find((tournament) => tournament.id === selectedTournamentId) || null,
        [tournaments, selectedTournamentId]
    );
    const tournamentHasStarted = Boolean(selectedTournament?.started);

    const groupPlayers = useMemo(() => {
        if (!selectedGroupId) {
            return [];
        }
        const group = groups.find((entry) => entry.id === selectedGroupId);
        if (!group) {
            return [];
        }
        const groupIds = group.teamIds || group.playerIds || [];
        return tournamentPlayers.filter((player) => groupIds.includes(player.id));
    }, [groups, selectedGroupId, tournamentPlayers]);

    const allGroupsCompleted = useMemo(() => {
        return Boolean(selectedTournamentId) && groups.length > 0 && groups.every((group) => group.completed);
    }, [groups, selectedTournamentId]);

    const refreshTournaments = useCallback(async () => {
        try {
            const data = sortTournamentsForOverview(normalizeArray(await api.get("/tournaments")));
            setTournaments(data);
            return data;
        } catch {
            setTournaments([]);
            return [];
        }
    }, [api]);

    const refreshTournamentPlayers = useCallback(async (tournamentId = selectedTournamentId) => {
        if (!tournamentId) {
            setTournamentPlayers([]);
            return [];
        }

        try {
            const data = normalizeArray(await api.get(`/tournaments/${tournamentId}/teams`));
            setTournamentPlayers(data);
            return data;
        } catch {
            setTournamentPlayers([]);
            return [];
        }
    }, [api, selectedTournamentId]);

    const refreshGroups = useCallback(async (tournamentId = selectedTournamentId) => {
        if (!tournamentId || !tournamentHasStarted) {
            setGroups([]);
            setSelectedGroupId(null);
            return [];
        }

        try {
            const data = normalizeArray(await api.get(`/tournaments/groups?tournamentId=${tournamentId}`));
            setGroups(data);
            setSelectedGroupId((currentGroupId) => (
                currentGroupId && data.some((group) => group.id === currentGroupId)
                    ? currentGroupId
                    : null
            ));
            return data;
        } catch (error) {
            setGroups([]);
            console.error("Fehler beim Laden der Gruppen:", error);
            return [];
        }
    }, [api, selectedTournamentId, tournamentHasStarted]);

    const reloadMatches = useCallback(async (groupId = selectedGroupId) => {
        if (!groupId) {
            setMatches([]);
            return [];
        }

        try {
            const data = normalizeArray(await api.get(`/tournaments/matches/group/${groupId}`));
            setMatches(data);
            await refreshGroups();
            return data;
        } catch (error) {
            console.error("Fehler beim Laden der Matches:", error);
            return [];
        }
    }, [api, refreshGroups, selectedGroupId]);

    const refreshMyOverview = useCallback(async (tournamentId = selectedTournamentId) => {
        if (!tournamentId || !user) {
            setMyOverview(null);
            setMyOverviewError(null);
            setMyOverviewLoading(false);
            return null;
        }

        setMyOverviewLoading(true);
        try {
            const data = await api.get(`/tournaments/${tournamentId}/my-overview`);
            setMyOverview(data);
            setMyOverviewError(null);
            return data;
        } catch (error) {
            setMyOverview(null);
            setMyOverviewError(error.message || "Persönliche Turnierdaten konnten nicht geladen werden.");
            return null;
        } finally {
            setMyOverviewLoading(false);
        }
    }, [api, selectedTournamentId, user]);

    const loadKnockoutPhaseExists = useCallback(async (tournamentId) => {
        if (!tournamentId) {
            return false;
        }

        try {
            return Boolean(await api.get(`/tournaments/knockout/${tournamentId}/exists`));
        } catch {
            return false;
        }
    }, [api]);

    const refreshKnockoutPhaseExists = useCallback(async (tournamentId = selectedTournamentId) => {
        if (!tournamentId) {
            setKnockoutPhaseExists(false);
            return false;
        }

        const exists = await loadKnockoutPhaseExists(tournamentId);
        setKnockoutPhaseExists(exists);
        return exists;
    }, [loadKnockoutPhaseExists, selectedTournamentId]);

    const loadKnockoutBracketPlanned = useCallback(async (tournamentId) => {
        if (!tournamentId) {
            return false;
        }

        try {
            return Boolean(await api.get(`/tournaments/knockout/${tournamentId}/planned`));
        } catch {
            return false;
        }
    }, [api]);

    const refreshKnockoutBracketPlanned = useCallback(async (tournamentId = selectedTournamentId) => {
        if (!tournamentId) {
            setKnockoutBracketPlanned(false);
            return false;
        }

        const planned = await loadKnockoutBracketPlanned(tournamentId);
        setKnockoutBracketPlanned(planned);
        return planned;
    }, [loadKnockoutBracketPlanned, selectedTournamentId]);

    useEffect(() => {
        refreshTournaments();
    }, [refreshTournaments]);

    const clearTournamentSelection = useCallback(() => {
        latestTournamentSelectionRef.current = null;
        filterAutoSetRef.current = false;
        setSelectedTournamentId(null);
        setTournamentPlayers([]);
        setGroups([]);
        setSelectedGroupId(null);
        setMatches([]);
        setSchedulerPair(null);
        setSelectedFilterPlayerId("");
        setEditingMatch(null);
        setKnockoutPhaseExists(false);
        setKnockoutBracketPlanned(false);
        setMyOverview(null);
        setMyOverviewError(null);
        setMyOverviewLoading(false);
        setActiveTournamentView("group");
        setIsTournamentSelectionExpanded(true);
        // URL-Parameter bereinigen
        navigate("/tournaments", {replace: true});
    }, [navigate]);

    const selectTournament = useCallback(async (tournamentId, {collapseSelection = true} = {}) => {
        latestTournamentSelectionRef.current = tournamentId;
        filterAutoSetRef.current = false;
        setSelectedTournamentId(tournamentId);
        setTournamentPlayers([]);
        setGroups([]);
        setSelectedGroupId(null);
        setMatches([]);
        setSchedulerPair(null);
        setSelectedFilterPlayerId("");
        setEditingMatch(null);
        setKnockoutPhaseExists(false);
        setKnockoutBracketPlanned(false);
        setActiveTournamentView("group");

        if (collapseSelection) {
            setIsTournamentSelectionExpanded(false);
        }

        const [knockoutPhaseAlreadyStarted, bracketPlanned] = await Promise.all([
            loadKnockoutPhaseExists(tournamentId),
            loadKnockoutBracketPlanned(tournamentId),
        ]);

        if (latestTournamentSelectionRef.current !== tournamentId) {
            return knockoutPhaseAlreadyStarted;
        }

        setKnockoutPhaseExists(knockoutPhaseAlreadyStarted);
        setKnockoutBracketPlanned(bracketPlanned);
        const tournament = tournaments.find((t) => t.id === tournamentId);
        const showKnockout = knockoutPhaseAlreadyStarted && (tournament?.groupPhaseClosed ?? true);
        setActiveTournamentView(showKnockout ? "knockout" : "group");
        return knockoutPhaseAlreadyStarted;
    }, [loadKnockoutBracketPlanned, loadKnockoutPhaseExists, tournaments]); // eslint-disable-line

    useEffect(() => {
        const tournamentIdParam = new URLSearchParams(window.location.search).get("tournamentId");
        if (!tournamentIdParam) {
            return;
        }

        const tournamentIdFromUrl = Number(tournamentIdParam);
        if (!Number.isFinite(tournamentIdFromUrl)) {
            return;
        }

        if (selectedTournamentId === tournamentIdFromUrl) {
            return;
        }

        selectTournament(tournamentIdFromUrl, {collapseSelection: true});
    }, [selectTournament, selectedTournamentId]); // eslint-disable-line

    useEffect(() => {
        refreshTournamentPlayers();
    }, [refreshTournamentPlayers]);

    useEffect(() => {
        refreshGroups();
    }, [refreshGroups]);

    useEffect(() => {
        if (tournamentHasStarted) {
            return;
        }
        setGroups([]);
        setSelectedGroupId(null);
        setMatches([]);
    }, [tournamentHasStarted]);

    useEffect(() => {
        reloadMatches();
    }, [reloadMatches]);

    useEffect(() => {
        refreshMyOverview();
    }, [refreshMyOverview]);

    useEffect(() => {
        refreshKnockoutPhaseExists();
    }, [refreshKnockoutPhaseExists]);

    useEffect(() => {
        refreshKnockoutBracketPlanned();
    }, [refreshKnockoutBracketPlanned]);

    useEffect(() => {
        if (!user || !myOverview?.registered) {
            return;
        }

        if (myOverview.ownTeamId != null && !filterAutoSetRef.current) {
            filterAutoSetRef.current = true;
            setSelectedFilterPlayerId(String(myOverview.ownTeamId));
        }

        if (
            selectedGroupId == null
            && deepLinkGroupId == null
            && myOverview.ownGroupId != null
            && groups.some((group) => group.id === myOverview.ownGroupId)
        ) {
            setSelectedGroupId(myOverview.ownGroupId);
        }
    }, [groups, myOverview, selectedGroupId, deepLinkGroupId, user]);

    // Deep-Link aus der Startseite: explizite Gruppe hat Vorrang vor der Auto-Erkennung der eigenen Gruppe.
    useEffect(() => {
        if (deepLinkGroupId == null || selectedGroupId === deepLinkGroupId) {
            return;
        }
        if (groups.some((group) => group.id === deepLinkGroupId)) {
            setSelectedGroupId(deepLinkGroupId);
        }
    }, [deepLinkGroupId, groups, selectedGroupId]);

    const consumeDeepLinkMatch = useCallback(() => {
        setDeepLinkMatchId(null);
        if (selectedTournamentId) {
            navigate(`/tournaments?tournamentId=${selectedTournamentId}`, {replace: true});
        }
    }, [navigate, selectedTournamentId]);

    // Deep-Link aus der Startseite: Gruppenphasen-Match öffnen, sobald es geladen ist.
    useEffect(() => {
        if (deepLinkMatchId == null || deepLinkGroupId == null) {
            return;
        }
        const match = matches.find((candidate) => candidate.id === deepLinkMatchId);
        if (match) {
            setEditingMatch(match);
            consumeDeepLinkMatch();
        }
    }, [deepLinkMatchId, deepLinkGroupId, matches, consumeDeepLinkMatch]);

    const handleSelectTournament = useCallback(async (tournamentId) => {
        if (selectedTournamentId === tournamentId) {
            clearTournamentSelection();
            return false;
        }
        await selectTournament(tournamentId, {collapseSelection: true});
        return true;
    }, [clearTournamentSelection, selectTournament, selectedTournamentId]);

    const handleResultChange = useCallback(async (matchId, setResults) => {
        try {
            const updatedMatch = await api.put(`/tournaments/matches/${matchId}/sets`, setResults);
            setMatches((previousMatches) => {
                const alreadyExists = previousMatches.some((match) => match.id === updatedMatch.id);
                if (alreadyExists) {
                    return previousMatches.map((match) => match.id === updatedMatch.id ? updatedMatch : match);
                }
                return [...previousMatches, updatedMatch];
            });
            await refreshGroups();
            return updatedMatch;
        } catch (error) {
            console.error("Fehler beim Speichern der Sätze:", error);
            throw new Error("Fehler beim Speichern der Sätze");
        }
    }, [api, refreshGroups]);

    const registerForTournament = useCallback(async () => {
        if (!selectedTournamentId) {
            return;
        }

        try {
            setRegistrationLoading(true);
            const overview = await api.post(`/tournaments/${selectedTournamentId}/register`);
            setMyOverview(overview);
            await refreshTournaments();
            await refreshTournamentPlayers(selectedTournamentId);
        } catch (error) {
            alert(error.message || "Die Anmeldung zum Turnier ist fehlgeschlagen.");
        } finally {
            setRegistrationLoading(false);
        }
    }, [api, refreshTournamentPlayers, refreshTournaments, selectedTournamentId]);

    const unregisterFromTournament = useCallback(async () => {
        if (!selectedTournamentId) {
            return;
        }

        try {
            setRegistrationLoading(true);
            const overview = await api.delete(`/tournaments/${selectedTournamentId}/register`);
            setMyOverview(overview);
            await refreshTournaments();
            await refreshTournamentPlayers(selectedTournamentId);
        } catch (error) {
            alert(error.message || "Die Abmeldung vom Turnier ist fehlgeschlagen.");
        } finally {
            setRegistrationLoading(false);
        }
    }, [api, refreshTournamentPlayers, refreshTournaments, selectedTournamentId]);

    const inviteByEmail = useCallback(async (email) => {
        if (!selectedTournamentId) {
            return;
        }
        await api.post(`/tournaments/${selectedTournamentId}/invite-by-email`, {email});
    }, [api, selectedTournamentId]);

    const initializeKnockoutPhase = useCallback(async () => {
        if (!selectedTournamentId) {
            return;
        }

        try {
            await api.post(`/tournaments/${selectedTournamentId}/close-group-phase`);
            setKnockoutPhaseExists(true);
            setKnockoutBracketPlanned(true);
            setActiveTournamentView("knockout");
            alert("Gruppenphase abgeschlossen — die K.O.-Phase zeigt jetzt die echten Teams an.");
        } catch (error) {
            alert(`Fehler beim Initialisieren der K.O.-Phase: ${error.message}`);
        }
    }, [api, selectedTournamentId]);

    const showKnockoutPhase = useCallback(() => {
        if (!selectedTournamentId || (!knockoutPhaseExists && !knockoutBracketPlanned)) {
            return;
        }
        setActiveTournamentView("knockout");
    }, [knockoutBracketPlanned, knockoutPhaseExists, selectedTournamentId]);

    const showGroupPhase = useCallback(() => {
        setActiveTournamentView("group");
    }, []);

    return {
        api,
        user,
        tournaments,
        selectedTournamentId,
        selectedTournament,
        tournamentPlayers,
        groups,
        selectedGroupId,
        groupPlayers,
        matches,
        schedulerPair,
        selectedFilterPlayerId,
        editingMatch,
        isTournamentSelectionExpanded,
        knockoutPhaseExists,
        knockoutBracketPlanned,
        activeTournamentView,
        allGroupsCompleted,
        myOverview,
        myOverviewLoading,
        myOverviewError,
        registrationLoading,
        tournamentHasStarted,
        deepLinkMatchId,
        deepLinkGroupId,
        consumeDeepLinkMatch,
        setSelectedGroupId,
        setSchedulerPair,
        setSelectedFilterPlayerId,
        setEditingMatch,
        setIsTournamentSelectionExpanded,
        handleSelectTournament,
        clearTournamentSelection,
        showKnockoutPhase,
        showGroupPhase,
        reloadMatches,
        handleResultChange,
        registerForTournament,
        unregisterFromTournament,
        inviteByEmail,
        initializeKnockoutPhase,
    };
};

